import { auth, db } from '../../firebase';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    confirmPasswordReset
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';

const AUTH_KEY = 'yathartha_auth';

export const authService = {
    // Login
    async login(email, password) {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Get additional user data from Firestore
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            let userData = userDoc.exists() ? userDoc.data() : null;

            // Force 'admin' role for this specific email
            if (user.email === 'admin@yatharthaweb.com') {
                if (userData) {
                    userData.role = 'admin';
                } else {
                    userData = {
                        id: user.uid,
                        email: user.email,
                        name: user.displayName || user.email.split('@')[0],
                        role: 'admin'
                    };
                }
            } else if (!userData) {
                // If no firestore doc and not admin, create basic user profile
                userData = {
                    id: user.uid,
                    email: user.email,
                    name: user.displayName || user.email.split('@')[0],
                    role: 'user'
                };
            }

            localStorage.setItem(AUTH_KEY, JSON.stringify(userData));
            return { success: true, data: userData };
        } catch (error) {
            // Fallback: Check if it's a franchise admin
            try {
                const q = query(
                    collection(db, 'franchiseAdmins'), 
                    where('email', '==', email), 
                    where('password', '==', password)
                );
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    const adminDoc = snapshot.docs[0];
                    const adminData = adminDoc.data();
                    
                    if (adminData.status !== 'active') {
                        return { success: false, error: "Your account is currently inactive. Please contact support." };
                    }

                    const userData = {
                        id: adminDoc.id,
                        email: email,
                        name: adminData.fullName,
                        role: 'franchise-admin',
                        franchiseData: adminData
                    };
                    localStorage.setItem(AUTH_KEY, JSON.stringify(userData));
                    return { success: true, data: userData };
                }
            } catch (err) {
                console.error("Franchise Login Check Error:", err);
            }

            console.error("Login Error:", error);
            let message = "Invalid email or password";
            if (error.code === 'auth/user-not-found') message = "User not found";
            if (error.code === 'auth/wrong-password') message = "Incorrect password";
            return { success: false, error: message };
        }
    },

    // Register
    async register(userData) {
        try {
            const { email, password, name } = userData;
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const newUserData = {
                id: user.uid,
                email,
                name: name || email.split('@')[0],
                role: 'user',
                createdAt: new Date().toISOString()
            };

            // Save to Firestore
            await setDoc(doc(db, 'users', user.uid), newUserData);

            localStorage.setItem(AUTH_KEY, JSON.stringify(newUserData));
            return { success: true, data: newUserData };
        } catch (error) {
            console.error("Registration Error:", error);
            return { success: false, error: error.message };
        }
    },

    // Logout
    async logout() {
        try {
            await signOut(auth);
            localStorage.removeItem(AUTH_KEY);
            localStorage.removeItem('diagnosisReport'); // Clear report data on logout
        } catch (error) {
            console.error("Logout Error:", error);
        }
    },

    // Get current user
    getCurrentUser() {
        const user = localStorage.getItem(AUTH_KEY);
        return user ? JSON.parse(user) : null;
    },

    // Check if authenticated
    isAuthenticated() {
        return !!auth.currentUser || !!this.getCurrentUser();
    },

    // Check if user is admin
    isAdmin() {
        const user = this.getCurrentUser();
        return user?.role === 'admin';
    },

    // Forgot password
    async forgotPassword(email) {
        try {
            await sendPasswordResetEmail(auth, email);
            return { success: true, message: 'Password reset link sent to email' };
        } catch (error) {
            console.error("Forgot Password Error:", error);
            return { success: false, error: error.message };
        }
    },

    // Reset password
    async resetPassword(oobCode, newPassword) {
        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            return { success: true, message: 'Password reset successful' };
        } catch (error) {
            console.error("Reset Password Error:", error);
            return { success: false, error: error.message };
        }
    }
};

export default authService;
